"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Loader, MessageCircle, Calendar, Clock, Users, Sparkles } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ReservationFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="146511" className="flex flex-col items-center justify-center space-y-6 rounded-none border border-primary/20 bg-card/30 p-12 text-center backdrop-blur-md">
        <div className="flex size-16 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Sparkles className="size-8" />
        </div>
        <div className="space-y-2">
          <h3 className="font-serif text-2xl font-light text-foreground">Awaiting Your Arrival</h3>
          <p className="text-muted-foreground font-sans text-sm tracking-wide">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="rounded-none border-primary/30 text-primary font-mono text-xs uppercase tracking-widest hover:bg-primary/10 hover:text-primary">
          New Reservation
        </Button>
      </div>;
  }
  const inputBaseClasses = "flex w-full rounded-none border-b border-t-0 border-x-0 border-border/50 bg-transparent px-0 py-3 text-sm text-foreground placeholder:text-muted-foreground/30 transition-all focus-visible:border-primary focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50";
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-6 sm:grid-cols-2">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Full Name</FormLabel>
              <FormControl>
                <input type="text" className={inputBaseClasses} placeholder="e.g. Erik Thorne" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Email Address</FormLabel>
              <FormControl>
                <input type="email" className={inputBaseClasses} placeholder="erik@nordicbite.dk" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Phone Number</FormLabel>
              <FormControl>
                <input type="tel" className={inputBaseClasses} placeholder="+45 00 00 00 00" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="guests" rules={{
        required: "Number of guests is required",
        min: {
          value: 1,
          message: "Minimum 1 guest"
        },
        max: {
          value: 20,
          message: "Maximum 20 guests"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Number of Guests</FormLabel>
              <Select onValueChange={val => field.onChange(Number(val))} defaultValue={field.value?.toString()}>
                <FormControl>
                  <SelectTrigger className="h-[46px] rounded-none border-b border-t-0 border-x-0 border-border/50 bg-transparent px-0 text-foreground focus:border-primary focus:ring-0">
                    <div className="flex items-center gap-2">
                      <Users className="size-4 text-muted-foreground" />
                      <SelectValue placeholder="Select party size" />
                    </div>
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="border-border/50 bg-card">
                  {[...Array(20)].map((_, i) => <SelectItem key={i + 1} value={(i + 1).toString()} data-index={i}>
                      {i + 1} {i === 0 ? 'Guest' : 'Guests'}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <FormField control={control} name="reservation_date" rules={{
        required: "Date is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Reservation Date</FormLabel>
              <FormControl>
                <div className="relative">
                  <SafeHydrate><Calendar className="absolute left-0 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" /></SafeHydrate>
                  <input type="date" className={`${inputBaseClasses} pl-8 [color-scheme:dark]`} {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="reservation_time" rules={{
        required: "Time is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Reservation Time</FormLabel>
              <FormControl>
                <div className="relative">
                  <Clock className="absolute left-0 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <input type="time" className={`${inputBaseClasses} pl-8 [color-scheme:dark]`} {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="special_requests" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Special Requests / Dietary Notes</FormLabel>
            <FormControl>
              <textarea className={`${inputBaseClasses} min-h-[100px] resize-none`} placeholder="Dietary requirements or special occasions..." {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-none border border-destructive/50 bg-destructive/5 p-4 text-xs font-mono uppercase tracking-wider text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="h-14 w-full rounded-none bg-foreground text-background font-mono uppercase tracking-[0.2em] text-xs hover:bg-foreground/90 transition-all">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Processing...
          </> : 'Book Now'}
      </Button>
    </Form>;
}
export default function TableReservations() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: cardRef,
    isVisible: isCardVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const phoneNumber = "+45 11 22 33 44";
  const cleanPhone = phoneNumber.replace(/\D/g, '');
  const whatsappUrl = `https://wa.me/${cleanPhone}`;
  return <section id="secure-your-table" className="dark relative overflow-hidden bg-background py-24 text-foreground sm:py-40">
      {}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-10">
        <div className="h-[600px] w-[600px] rounded-full bg-primary/10 blur-[150px]" />
        <div className="absolute left-1/4 top-0 h-[300px] w-[300px] rounded-full bg-primary/5 blur-[100px]" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className={`mb-20 max-w-2xl transition-all duration-1000 ${isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <h2 className="font-serif text-5xl font-light tracking-tight text-foreground sm:text-7xl">
            Secure Your <span className="italic text-primary">Gathering</span>
          </h2>
          <p className="mt-6 text-lg text-muted-foreground font-sans tracking-wide leading-relaxed">
            Experience the essence of the North. Submit your request below, and our team will curate a table that mirrors the stillness and beauty of our seasonal menu.
          </p>
        </div>

        <div className="grid gap-16 lg:grid-cols-12 lg:gap-16">
          {}
          <div ref={formRef} className={`lg:col-span-7 xl:col-span-8 transition-all duration-1000 delay-100 ${isFormVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
            <div className="rounded-none border-none bg-card/10 p-2 sm:p-0 backdrop-blur-sm">
              <FormProvider formKey="reservation_form_nordic_bite" formId="reservation_form_nordic_bite" sectionName="Table Reservations" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              reservation_date: "",
              reservation_time: "",
              guests: 2,
              special_requests: ""
            }} validationRules={{
              full_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true
              },
              reservation_date: {
                required: true
              },
              reservation_time: {
                required: true
              },
              guests: {
                required: true,
                min: 1,
                max: 20
              },
              special_requests: {}
            }} submitText="Book Now" successMessage="Your table request has been received. We will contact you via email to confirm your reservation.">
                <ReservationFormInner />
              </FormProvider>
            </div>
          </div>

          {}
          <div ref={cardRef} className={`lg:col-span-5 xl:col-span-4 transition-all duration-1000 delay-300 ${isCardVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
            <div className="sticky top-32">
              <Card className="overflow-hidden border-none bg-card/40 backdrop-blur-xl rounded-none">
                <div className="h-0.5 w-full bg-primary/30" />
                <CardHeader className="text-center pb-4 pt-10">
                  <div className="mx-auto mb-6 flex justify-center">
                    <SafeHydrate><Avatar className="size-28 border border-primary/20 grayscale hover:grayscale-0 transition-all duration-700">
                      <AvatarImage src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1602682/img/vip_concierge_manager.png" alt="Maître d'Hôtel" className="object-cover" />
                      <AvatarFallback className="bg-muted text-primary font-serif text-2xl">NB</AvatarFallback>
                    </Avatar></SafeHydrate>
                  </div>
                  <CardTitle className="font-serif text-3xl font-light text-foreground">Søren Jensen</CardTitle>
                  <CardDescription className="text-primary uppercase tracking-[0.3em] text-[10px] font-mono mt-2">
                    Maître d'Hôtel
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-8 text-center pt-4 pb-10">
                  <p className="text-sm text-muted-foreground font-sans leading-relaxed px-4">
                    For private events, specific dietary inquiries, or immediate assistance with your evening, please reach out directly.
                  </p>
                  
                  <div className="space-y-6 px-4">
                    <Button asChild variant="outline" className="h-14 w-full rounded-none border-primary/40 bg-transparent text-primary hover:bg-primary hover:text-background transition-all duration-500 group">
                      <Link to={whatsappUrl} newTab>
                        <MessageCircle className="mr-3 size-4 transition-transform group-hover:scale-110" />
                        <span className="font-mono text-xs uppercase tracking-widest">Inquire via WhatsApp</span>
                      </Link>
                    </Button>
                    
                    <div className="flex items-center justify-center gap-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground/60">
                      <span className="inline-block size-1.5 rounded-full bg-primary/60 animate-pulse" />
                      Tues-Sun, 17:00 — 23:00
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>;
}